import {
  type AnyPgColumn,
  boolean,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  doublePrecision,
  uuid,
  uniqueIndex,
  vector,
  primaryKey,
  jsonb,
} from "drizzle-orm/pg-core";
import { TIconName } from "~/lib/icon-names";

export enum MembershipPlan {
  Standard = "Standard",
  Pro = "Pro",
  ProPlus = "ProPlus",
}

export enum CoachEmbeddingType {
  AboutMe = "AboutMe",
  Offer = "Offer",
  Testimonial = "Testimonial",
  PreferencesMatch = "PreferencesMatch",
}

export enum AppointmentStatus {
  Scheduled = "scheduled",
  Completed = "completed",
  Cancelled = "cancelled",
  NoShow = "noshow",
  Rescheduled = "rescheduled",
}

export enum ExpertiseType {
  Primary = "primary",
  Secondary = "secondary",
}

export enum CCOType {
  PAO = "pao",
  CTO = "cto",
}

export function enumToPgEnum<T extends Record<string, string>>(
  myEnum: T,
): [T[keyof T], ...T[keyof T][]] {
  // Capture enum values directly as string literals
  return Object.values(myEnum) as [T[keyof T], ...T[keyof T][]];
}

export const membershipPlanEnum = pgEnum(
  "membership_plan",
  enumToPgEnum(MembershipPlan),
);

export const coachEmbeddingTypeEnum = pgEnum(
  "coach_embedding_type",
  enumToPgEnum(CoachEmbeddingType),
);

export const appointmentStatusEnum = pgEnum(
  "appointment_status",
  enumToPgEnum(AppointmentStatus),
);

export const expertiseTypeEnum = pgEnum(
  "expertise_type",
  enumToPgEnum(ExpertiseType),
);

export const ccoTypeEnum = pgEnum("cco_type", enumToPgEnum(CCOType));

const timestamps = {
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" })
    .defaultNow()
    .$onUpdate(() => new Date()),
} as const;

export const coaches = pgTable("Coach", {
  id: uuid("id").primaryKey().defaultRandom(),
  username: text("username").notNull().unique(),

  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  fullName: text("full_name").notNull(),

  email: text("email").notNull().unique(),
  phone: text("phone"),

  profilePictureUrl: text("profile_picture_url"),

  ghlContactId: text("ghl_contact_id").notNull().unique(),
  ghlLocationId: text("ghl_location_id").unique(),
  ghlCalendarId: text("ghl_calendar_id"),
  ghlUserId: text("ghl_user_id"),

  profileId: uuid("profile_id"),
  mainOfferId: uuid("main_offer_id"),
  coachDetailsId: uuid("coach_details_id"),

  source: text("source"),

  membershipPlan: membershipPlanEnum("membership_plan")
    .notNull()
    .default(MembershipPlan.Standard),
  stripeCustomerId: text("stripe_customer_id").unique(),
  stripeSubscriptionId: text("stripe_subscription_id").unique(),
  subscriptionStatus: text("subscription_status"),

  stripeAccountId: text("stripe_account_id"),
  stripeChargesEnabled: boolean("stripe_charges_enabled"),
  stripePayoutsEnabled: boolean("stripe_payouts_enabled"),
  // track manual profile edits and onboarding completion
  profile_manually_edited: boolean("profile_manually_edited")
    .notNull()
    .default(false),
  onboarding_completed_at: timestamp("onboarding_completed_at", {
    mode: "date",
  }),
  contractSigned: boolean("contract_signed").default(false),

  paymentTestApiKey: text("payment_test_api_key"),
  paymentTestPublishableKey: text("payment_test_publishable_key"),
  paymentLiveApiKey: text("payment_live_api_key"),
  paymentLivePublishableKey: text("payment_live_publishable_key"),

  referralCode: text("referral_code"),
  referredByCoachId: uuid("referred_by_coach_id").references(
    (): AnyPgColumn => coaches.id,
  ),

  featureResources: boolean("feature_resources"),

  isLifetimeMember: boolean("is_lifetime_member").default(false),
  lifetimePurchaseDate: timestamp("lifetime_purchase_date", { mode: "date" }),

  ...timestamps,
});

export type CoachInsert = typeof coaches.$inferInsert;
export type CoachSelect = typeof coaches.$inferSelect;

export const offers = pgTable("Offer", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  // Main headlines and descriptions
  preHeadline: text("pre_headline"),
  headline: text("headline"),
  description: text("description"),

  // Direct sale configuration
  directSaleEnabled: boolean("direct_sale_enabled").default(false),
  price: integer("price"), // stored in cents
  currency: text("currency").default("USD"),
  originalPrice: integer("original_price"), // stored in cents, for strikethrough price

  // Struggles
  strugglesHeadline: text("struggles_headline"),
  strugglesDescription: text("struggles_description"),
  strugglesImageUrl: text("struggles_image_url"),

  // Results section
  resultsPreHeadline: text("results_pre_header").default(
    "Results you can expect",
  ),
  resultsHeadline: text("results_headline"),
  resultsDescription: text("results_description"),
  resultsImageUrl: text("results_image_url"),

  // Program details
  programHeadline: text("program_headline"),
  programDescription: text("program_description"),
  programImageUrl: text("program_image_url"),

  // Bottom CTA
  bottomCtaHeadline: text("bottom_cta_headline"),
  bottomCtaText: text("bottom_cta_text"),

  // Dates
  applicationDeadline: timestamp("application_deadline"),
  cohortStartDate: timestamp("cohort_start_date"),

  // Media
  vslVideoUrl: text("vsl_video_url"),

  // Guarantee
  satisfactionGuaranteeText: text("satisfaction_guarantee_text"),
});

export type OfferInsert = typeof offers.$inferInsert;
export type OfferSelect = typeof offers.$inferSelect;

export const offerBenefits = pgTable("OfferBenefit", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  content: text("content"),
  imageSrc: text("image_src"),
  order: integer("order").notNull().default(0),
});

export type OfferBenefitInsert = typeof offerBenefits.$inferInsert;
export type OfferBenefitSelect = typeof offerBenefits.$inferSelect;

export const offerComponents = pgTable("OfferComponent", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),

  preHeader: text("pre_header"),
  title: text("title").notNull(),
  content: text("content").notNull(),
  imageUrl: text("image_url"),
  order: integer("order").notNull().default(0),
});

export type OfferComponentInsert = typeof offerComponents.$inferInsert;
export type OfferComponentSelect = typeof offerComponents.$inferSelect;

export const offerTags = pgTable("OfferTag", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  property: text("property").notNull(),
  order: integer("order").notNull().default(0),
});

export type OfferTagInsert = typeof offerTags.$inferInsert;
export type OfferTagSelect = typeof offerTags.$inferSelect;

export const offerBonuses = pgTable("OfferBonus", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  content: text("content").notNull(),
  imageSrc: text("image_src"),
  order: integer("order").notNull().default(0),
});

export type OfferBonusInsert = typeof offerBonuses.$inferInsert;
export type OfferBonusSelect = typeof offerBonuses.$inferSelect;

export const offerFaqs = pgTable("OfferFaq", {
  id: uuid("id").primaryKey().defaultRandom(),
  offerId: uuid("offer_id")
    .notNull()
    .references(() => offers.id, { onDelete: "cascade" }),
  question: text("question").notNull(),
  answer: text("answer").notNull(),
  order: integer("order").notNull().default(0),
});

export type OfferFaqInsert = typeof offerFaqs.$inferInsert;
export type OfferFaqSelect = typeof offerFaqs.$inferSelect;

export const testimonials = pgTable("Testimonial", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  content: text("content").notNull(),
  author: text("author").notNull(),
  order: integer("order").notNull().default(0),
  ...timestamps,
});

export type TestimonialInsert = typeof testimonials.$inferInsert;
export type TestimonialSelect = typeof testimonials.$inferSelect;

export const profiles = pgTable("Profile", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" })
    .unique(),

  isPublic: boolean("is_public").notNull().default(false),
  isPreGeneratedProfile: boolean("is_pre_generated_profile")
    .notNull()
    .default(false),

  backgroundInformation: text("background_information"),

  brandName: text("brand_name"),
  brandLogoUrl: text("brand_logo_url"),

  coachingTitle: text("coaching_title"),
  languages: text("languages"),
  formOfConduct: text("coaching_level"),
  coachingStyle: text("coaching_style"),
  primaryCity: text("primary_city"),
  primaryCountry: text("primary_country"),
  closestMajorCity: text("closest_major_city"),

  heroImageSrc: text("hero_image_src"),
  openingStatement: text("opening_statement"),
  mainImage: text("main_image"),
  mainVideo: text("main_video"),

  preHeading: text("pre_heading"),
  title: text("title"),
  description: text("introduction"),

  facebookProfile: text("facebook_profile"),
  instagramProfile: text("instagram_profile"),
  linkedinProfile: text("linkedin_profile"),
  website: text("website"),
  youtubeChannel: text("youtube_channel"),

  whyTitle: text("why_title"),
  whyContent: text("why_content"),

  ctaButtonText: text("cta_button_text").default("Book a discovery call"),

  leadMagnetTitle: text("lead_magnet_title"),
  leadMagnetDescription: text("lead_magnet_description"),
  leadMagnetCtaText: text("lead_magnet_cta_text"),
  leadMagnetUrl: text("lead_magnet_url"),
});

export type ProfileInsert = typeof profiles.$inferInsert;
export type ProfileSelect = typeof profiles.$inferSelect;

export const coachEmbeddings = pgTable(
  "CoachEmbeddings",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    type: coachEmbeddingTypeEnum("type").notNull(),
    embedding: vector("embedding", { dimensions: 1536 }).notNull(),
  },
  (table) => ({
    embeddingIndex: index("embedding_index").using(
      "hnsw",
      table.embedding.op("vector_cosine_ops"),
    ),
    uniqueCoachType: uniqueIndex("coach_type_unique").on(
      table.coachId,
      table.type,
    ),
  }),
);

export const coachDetails = pgTable("CoachDetails", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" })
    .unique(),

  dateOfBirth: timestamp("date_of_birth"),
  gender: text("gender"),

  mainCoachingCategory: text("main_coaching_category"),
  commonChallenges: text("common_challenges"),
  typicalTimeline: text("typical_timeline"),
  typesOfCoaching: text("types_of_coaching").array(),
  typesOfClients: text("types_of_clients").array(),
  typesOfProblems: text("types_of_problems").array(),

  // Coaching Format & Delivery
  coachingFormats: text("coaching_formats").array().default([]),
  sessionDeliveryMethods: text("session_delivery_methods").array().default([]),

  // Experience Level & Coaching Packages
  experienceLevel: text("experience_level"),

  // Coaching Style & Approach
  coachingApproaches: text("coaching_approaches").array().default([]),

  // Faith-Based Coaching
  faithIntegration: text("faith_integration").array().default([]),

  // Personality Profile
  discType: text("disc_type"),
  mbtiType: text("mbti_type"),
  enneagramType: text("enneagram_type"),
  humanDesignType: text("human_design_type"),

  joinedCommunityAt: timestamp("joined_community_at", { mode: "date" }),
});

export type CoachDetailsInsert = typeof coachDetails.$inferInsert;
export type CoachDetailsSelect = typeof coachDetails.$inferSelect;

export const certifications = pgTable("Certifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  issuer: text("issuer").notNull(),
  imageSrc: text("image_src"),
  verified: boolean("verified").notNull(),
  url: text("url"),
  order: integer("order").notNull().default(0),
});

export type CertificationsInsert = typeof certifications.$inferInsert;
export type CertificationsSelect = typeof certifications.$inferSelect;

export const ghlAgencyTokens = pgTable("GHLAgencyToken", {
  id: uuid("id").primaryKey().defaultRandom(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at", { mode: "date" }).notNull(),
  companyId: text("company_id").notNull(),
  locationId: text("location_id"),
  userId: text("user_id"),
  tokenType: text("token_type", { enum: ["Company", "Location"] }).notNull(),
  ...timestamps,
});

export type GHLAgencyTokenInsert = typeof ghlAgencyTokens.$inferInsert;
export type GHLAgencyTokenSelect = typeof ghlAgencyTokens.$inferSelect;

export const coachingResults = pgTable("CoachingResults", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  description: text("description").notNull(),
  icon: text("icon").notNull(),
  order: integer("order").notNull().default(0),
});

export type CoachingResultsInsert = typeof coachingResults.$inferInsert;
export type CoachingResultsSelect = typeof coachingResults.$inferSelect;

export const invoices = pgTable("Invoice", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id),

  ...timestamps,
  dueOn: timestamp("due_on").notNull(),
  paidOn: timestamp("paid_on"),

  customerName: text("customer_name").notNull(),
  customerEmail: text("customer_email").notNull(),
  description: text("description").notNull(),
  amount: integer("amount").notNull(), // in cents
  currency: text("currency").notNull(),
  status: text("status").notNull(),

  stripeInvoiceId: text("stripe_invoice_id").notNull(),
  stripeChargeId: text("stripe_charge_id"),
  url: text("url"),
});

export type InvoiceInsert = typeof invoices.$inferInsert;
export type InvoiceSelect = typeof invoices.$inferSelect;

export const resources = pgTable("Resource", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  title: text("title").notNull(),
  content: text("content").notNull(),
  description: text("description").notNull(),
  imageSrc: text("image_src"),
  publishedAt: timestamp("published_at", { mode: "date" }).defaultNow(),

  externalLinkText: text("external_link_text"),
  externalLinkUrl: text("external_link_url"),
  ...timestamps,
});

export type ResourceInsert = typeof resources.$inferInsert;
export type ResourceSelect = typeof resources.$inferSelect;

export const coachPoints = pgTable("CoachPoints", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),
  points: integer("points").notNull(),
  type: text("type").notNull(), // 'earned' or 'redeemed'
  description: text("description").notNull(),
  ...timestamps,
});

export type CoachPointsInsert = typeof coachPoints.$inferInsert;
export type CoachPointsSelect = typeof coachPoints.$inferSelect;

// Users table
export const users = pgTable("User", {
  id: uuid("id").primaryKey().defaultRandom(),
  email: text("email").unique(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  phoneNumber: text("phone_number"),
  profilePictureUrl: text("profile_picture_url"),
  clerkUserId: text("clerk_user_id"),

  ...timestamps,
});

export type UserInsert = typeof users.$inferInsert;
export type UserSelect = typeof users.$inferSelect;

export const userPreferences = pgTable(
  "UserPreferences",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: uuid("user_id")
      .notNull()
      .references(() => users.id),
    goals: text("goals").array().notNull().default([]),
    otherGoal: text("other_goal"),
    startTime: text("start_time").notNull().default(""),
    support: text("support").notNull().default(""),

    coachingFormats: text("coaching_formats").notNull().default(""),
    coachingApproaches: text("coaching_approaches")
      .array()
      .notNull()
      .default([]),
    coachingPackages: text("coaching_packages").array().notNull().default([]),
    coachGender: text("coach_gender").notNull().default(""),
    coachAges: text("coach_ages").array().notNull().default([]),
    userPersonality: text("user_rersonality").array().notNull().default([]),
    coachPersonality: text("coach_rersonality").array().notNull().default([]),
    sessionDeliveryMethods: text("session_delivery_methods")
      .notNull()
      .default(""),
    prefersCoachLocation: text("prefers_coach_location")
      .notNull()
      .default("No preference"),
    coachLocationCountry: text("coach_location_country"),
    coachLocationRegion: text("coach_location_region"),
    faithIntegration: text("faith_integration").array().notNull().default([]),
    otherFaithIntegration: text("other_faith_integration"),
    preferredLanguages: text("preferred_languages")
      .array()
      .notNull()
      .default([]),
    personalContext: text("personal_context"),
    ...timestamps,
    preferencesEmbedding: vector("preferences_embedding", {
      dimensions: 1536,
    }).notNull(),
  },
  (table) => ({
    preferencesEmbeddingIndex: index("preferences_embedding_index").using(
      "hnsw",
      table.preferencesEmbedding.op("vector_cosine_ops"),
    ),
  }),
);

export type UserPreferencesInsert = typeof userPreferences.$inferInsert;
export type UserPreferencesSelect = typeof userPreferences.$inferSelect;

export const appointments = pgTable("Appointment", {
  id: uuid("id").primaryKey().defaultRandom(),
  coachId: uuid("coach_id")
    .notNull()
    .references(() => coaches.id, { onDelete: "cascade" }),

  ghlAppointmentId: text("ghl_appointment_id").notNull().unique(),
  ghlContactId: text("ghl_contact_id").notNull(),
  ghlCalendarId: text("ghl_calendar_id").notNull(),

  status: appointmentStatusEnum("status").notNull(),
  startTime: timestamp("start_time", { mode: "date" }).notNull(),
  endTime: timestamp("end_time", { mode: "date" }).notNull(),

  title: text("title").notNull(),
  description: text("description"),

  metadata: text("metadata"), // JSON stringified GHL-specific data

  ...timestamps,
});

export type AppointmentInsert = typeof appointments.$inferInsert;
export type AppointmentSelect = typeof appointments.$inferSelect;

export const expertiseCategories = pgTable("expertise_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull().unique(),
  listingName: text("listing_name"), // New column for plural/listing contexts
  slug: text("slug").notNull().unique(),
  icon: text("icon").$type<TIconName>(), // Icon name for NamedIcon
  description: text("description"), // Optional description for the category
});

export type ExpertiseCategoryInsert = typeof expertiseCategories.$inferInsert;
export type ExpertiseCategorySelect = typeof expertiseCategories.$inferSelect;

export const expertiseSubcategories = pgTable("expertise_subcategories", {
  id: uuid("id").primaryKey().defaultRandom(),
  categoryId: uuid("category_id")
    .notNull()
    .references(() => expertiseCategories.id),
  name: text("name").notNull().unique(),
  listingName: text("listing_name"), // New column for plural/listing contexts
  slug: text("slug").notNull().unique(),
});

export type ExpertiseSubcategoryInsert =
  typeof expertiseSubcategories.$inferInsert;
export type ExpertiseSubcategorySelect =
  typeof expertiseSubcategories.$inferSelect;

export const coachExpertise = pgTable(
  "coach_expertise",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    subcategoryId: uuid("subcategory_id")
      .notNull()
      .references(() => expertiseSubcategories.id, { onDelete: "cascade" }),
    type: expertiseTypeEnum("type").notNull().default(ExpertiseType.Secondary),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.subcategoryId] }),
    index("idx_subcat").on(table.subcategoryId),
    index("idx_type").on(table.type),
  ],
);

export type CoachExpertiseInsert = typeof coachExpertise.$inferInsert;
export type CoachExpertiseSelect = typeof coachExpertise.$inferSelect;

// Coach Certifying Organizations (CCOs)
export const ccos = pgTable(
  "ccos",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull().unique(),
    slug: text("slug").notNull().unique(),
    website: text("website"),
    logoUrl: text("logo_url"),
    description: text("description"),
    type: ccoTypeEnum("type").notNull().default(CCOType.CTO),
    subcategory: text("subcategory"),
    ...timestamps,
  },
  (table) => ({
    nameIdx: index("cco_name_idx").on(table.name),
    slugIdx: index("cco_slug_idx").on(table.slug),
  }),
);

export type CcoInsert = typeof ccos.$inferInsert;
export type CcoSelect = typeof ccos.$inferSelect;

// Join table for coaches and CCOs
export const coachesCcos = pgTable(
  "coaches_ccos",
  {
    coachId: uuid("coach_id")
      .notNull()
      .references(() => coaches.id, { onDelete: "cascade" }),
    ccoId: uuid("cco_id")
      .notNull()
      .references(() => ccos.id, { onDelete: "cascade" }),
    membershipVerifiedAt: timestamp("membership_verified_at", { mode: "date" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.coachId, table.ccoId] }),
    index("cco_coach_idx").on(table.coachId),
    index("cco_cco_idx").on(table.ccoId),
  ],
);

export type CoachCcoInsert = typeof coachesCcos.$inferInsert;
export type CoachCcoSelect = typeof coachesCcos.$inferSelect;

export const cities = pgTable("cities", {
  id: uuid("id").primaryKey().defaultRandom(),
  slug: text("slug").notNull().unique(),
  label: text("label").notNull(),
  country: text("country").notNull(),
  region: text("region").notNull(),
  latitude: doublePrecision("latitude").notNull(),
  longitude: doublePrecision("longitude").notNull(),
});

export type CityInsert = typeof cities.$inferInsert;
export type CitySelect = typeof cities.$inferSelect;

export const coachGroupPages = pgTable(
  "coach_group_pages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    groupType: text("group_type").notNull(),
    citySlug: text("city_slug").references(() => cities.slug),
    categorySlug: text("category_slug").references(
      () => expertiseCategories.slug,
    ),
    subcategorySlug: text("subcategory_slug").references(
      () => expertiseSubcategories.slug,
    ),
    seoTitle: text("seo_title"),
    metaDescription: text("meta_description"),
    contentMd: text("content_md"),
    faqItems: jsonb("faq_items").$type<
      {
        question: string;
        answer: string;
      }[]
    >(),
    ...timestamps,
  },
  (table) => ({
    uniqueGroup: uniqueIndex("coach_group_pages_unique").on(
      table.groupType,
      table.citySlug,
      table.categorySlug,
      table.subcategorySlug,
    ),
  }),
);

export type CoachGroupPageInsert = typeof coachGroupPages.$inferInsert;
export type CoachGroupPageSelect = typeof coachGroupPages.$inferSelect;
